import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Equal, Plus, X, Loader2, BookA } from "lucide-react";

// THE CREW'S OWN SYNONYMS (Search S1): anyone who can search suggests one; it is used once someone allowed approves it.
export function SynonymSuggestButton({ testid, word = "", cameFrom = "search_box", label = "Suggest a word that means the same", className = "" }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" data-testid={testid} onClick={() => setOpen(true)}
        className={className || "inline-flex items-center gap-1.5 rounded-full h-9 px-3 text-xs font-medium border border-slate-200 text-slate-600 hover:border-[#E0781A] hover:text-[#B45309] transition-colors"}>
        <BookA className="w-3.5 h-3.5" />{label}
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid={`${testid}-dialog`} className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display">Words that mean the same</DialogTitle>
            <DialogDescription>Search will find either word. It's used once someone allowed to approve synonyms says yes.</DialogDescription>
          </DialogHeader>
          {open && <SynonymForm testid={testid} word={word} cameFrom={cameFrom} onDone={() => setOpen(false)} />}
        </DialogContent>
      </Dialog>
    </>
  );
}

export function SynonymForm({ testid, word = "", cameFrom = "search_box", onDone }) {
  const [terms, setTerms] = useState([word, ""]);
  const [known, setKnown] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!word.trim()) return;
    api.get("/search-synonyms", { params: { word } }).then(({ data }) => setKnown(data.items || [])).catch(() => {});
  }, [word]);
  const set = (i, v) => setTerms((t) => t.map((x, j) => (j === i ? v : x)));
  const filled = terms.filter((t) => t.trim());
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/search-synonyms", { terms: filled, came_from: cameFrom });
      toast.success(data.status === "approved" ? `In use now: ${data.terms.join(" = ")}` : `Sent for approval: ${data.terms.join(" = ")}`);
      setTerms([word, ""]);
      onDone && onDone(data);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-3" data-testid={`${testid}-form`}>
      {known.length > 0 && (
        <div data-testid={`${testid}-known`} className="rounded-lg border border-slate-200 bg-slate-50 p-2.5 text-xs text-slate-600 space-y-1">
          {known.map((k) => (
            <div key={k.id}><b className="text-slate-800">{k.terms.join(" = ")}</b> <span className="text-slate-400">· {k.status === "approved" ? "in use" : "waiting for approval"}</span></div>
          ))}
        </div>
      )}
      {terms.map((t, i) => (
        <div key={i} className="flex items-center gap-2">
          {i > 0 && <Equal className="w-4 h-4 text-slate-400 shrink-0" />}
          <Input data-testid={`${testid}-term-${i}`} value={t} maxLength={40} onChange={(e) => set(i, e.target.value)}
            placeholder={i === 0 ? "e.g. turbo" : "e.g. turbocharger"} className="h-11 text-base" />
          {i > 1 && (
            <button type="button" data-testid={`${testid}-term-remove-${i}`} onClick={() => setTerms((x) => x.filter((_, j) => j !== i))}
              className="h-11 w-11 shrink-0 rounded-lg border border-slate-200 text-slate-400 hover:text-[#B01E28] flex items-center justify-center"><X className="w-4 h-4" /></button>
          )}
        </div>
      ))}
      <div className="flex items-center justify-between gap-2 pt-1">
        {terms.length < 6
          ? <button type="button" data-testid={`${testid}-add-term`} onClick={() => setTerms((x) => [...x, ""])}
              className="inline-flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-slate-800"><Plus className="w-3.5 h-3.5" />Another word</button>
          : <span />}
        <Button data-testid={`${testid}-submit`} type="submit" disabled={busy || filled.length < 2}
          className="h-11 rounded-lg bg-[#E0781A] hover:bg-[#C76714] text-white">
          {busy && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}Save
        </Button>
      </div>
    </form>
  );
}
